import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { useHubModuleWriteAny } from "@/lib/useHubModuleWrite";

import { createClampQcReQcWorkOrders, type ClampQcReQcLineInput } from "./clampQcReQcApi";

type ReQcDraftLine = ClampQcReQcLineInput & { key: string };

function emptyLine(size: "small" | "large" = "small"): ReQcDraftLine {
  return { key: crypto.randomUUID(), size, quantity: 1 };
}

type ClampQcReQcPanelProps = {
  defaultMfrReferenceNumber?: string | null;
};

export function ClampQcReQcPanel({ defaultMfrReferenceNumber }: ClampQcReQcPanelProps) {
  const canWrite = useHubModuleWriteAny(["clampqc", "manufacturing", "manufacturing_management", "quality"]);
  const { showToast } = useHubToast();
  const [mfrReferenceNumber, setMfrReferenceNumber] = useState(defaultMfrReferenceNumber?.trim() ?? "");
  const [notes, setNotes] = useState("Re-QC after clamp repair");
  const [lines, setLines] = useState<ReQcDraftLine[]>([emptyLine("small"), emptyLine("large")]);
  const [createdSlugs, setCreatedSlugs] = useState<Array<{ slug: string; label: string }>>([]);

  const createMutation = useMutation({
    mutationFn: () =>
      createClampQcReQcWorkOrders({
        mfrReferenceNumber,
        notes,
        lines: lines.map((line) => ({ size: line.size, quantity: line.quantity })),
      }),
    onSuccess: (result) => {
      setCreatedSlugs(
        result.workOrders.map((row) => ({
          slug: row.slug,
          label: row.workOrderNumber ?? row.slug,
        })),
      );
      showToast({
        tone: "success",
        message: `Created ${String(result.workOrders.length)} re-QC work order(s) and allocated materials.`,
      });
    },
    onError: (error) => {
      showToast({
        tone: "warning",
        message: error instanceof ApiError ? error.message : "Could not create re-QC work orders.",
      });
    },
  });

  if (!canWrite) {
    return null;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-800">
      <p className="font-semibold text-slate-900">Create re-QC work orders</p>
      <p className="mt-1 text-slate-600">
        After repair, create new clamp QC jobs by line size. Each row becomes a work order with materials auto-allocated.
      </p>

      <div className="mt-3 space-y-3">
        <div>
          <Label htmlFor="clamp-reqc-mfr-ref">Manufacturer reference</Label>
          <Input
            id="clamp-reqc-mfr-ref"
            value={mfrReferenceNumber}
            onChange={(event) => setMfrReferenceNumber(event.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="clamp-reqc-notes">Notes</Label>
          <Input
            id="clamp-reqc-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="mt-1"
          />
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Re-QC matrix</p>
          {lines.map((line, index) => (
            <div key={line.key} className="flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor={`clamp-reqc-size-${line.key}`}>Line</Label>
                <select
                  id={`clamp-reqc-size-${line.key}`}
                  className="mt-1 block min-h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  value={line.size}
                  onChange={(event) => {
                    const size = event.target.value === "large" ? "large" : "small";
                    setLines((previous) =>
                      previous.map((entry, entryIndex) =>
                        entryIndex === index ? { ...entry, size } : entry,
                      ),
                    );
                  }}
                >
                  <option value="small">Small (90-002-TLA)</option>
                  <option value="large">Large (90-003-TLA)</option>
                </select>
              </div>
              <div>
                <Label htmlFor={`clamp-reqc-qty-${line.key}`}>Quantity</Label>
                <Input
                  id={`clamp-reqc-qty-${line.key}`}
                  type="number"
                  min={1}
                  value={String(line.quantity)}
                  onChange={(event) => {
                    const quantity = Number.parseInt(event.target.value, 10);
                    setLines((previous) =>
                      previous.map((entry, entryIndex) =>
                        entryIndex === index
                          ? { ...entry, quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 }
                          : entry,
                      ),
                    );
                  }}
                  className="mt-1 w-28"
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                disabled={lines.length <= 1}
                onClick={() => setLines((previous) => previous.filter((entry) => entry.key !== line.key))}
              >
                <Trash2 aria-hidden className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="secondary" onClick={() => setLines((previous) => [...previous, emptyLine()])}>
            <Plus aria-hidden className="mr-1 h-4 w-4" />
            Add row
          </Button>
        </div>

        <Button
          type="button"
          disabled={createMutation.isPending || mfrReferenceNumber.trim() === ""}
          onClick={() => createMutation.mutate()}
        >
          {createMutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              Creating…
            </span>
          ) : (
            "Create and allocate re-QC work orders"
          )}
        </Button>

        {createdSlugs.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {createdSlugs.map((row) => (
              <li key={row.slug}>
                <Link
                  to={`/clamp-qc/jobs/${encodeURIComponent(row.slug)}`}
                  className="font-medium text-brand-700 hover:text-brand-800"
                >
                  Open {row.label}
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
